import { type ComputedRef, type Ref, computed, ref } from "vue";



import { Factory } from "@/data/dao/Factory";
import type { DaoError } from "@/data/dao/service/BaseServiceDao";
import { CustomerDao } from "@/data/dao/service/CustomerDao";
import { dataEvents } from "@/data/events/dataEvents";
import type { IPaymentIntent, IPaymentMethod } from "@/data/model/Customer";
import { UiPaymentState, UserItemSelectionChangeReason } from "@/data/model/Enums";
import type { BillItemSelectionChangedEvent } from "@/data/model/Events";
import { onApplyDiscountErrorOccurred } from "@/data/services/DiscountsService";
import { getIsApplyVouchersAllowedForOrderUseCase, getIsApplyVouchersAllowedForPayUseCase } from "@/data/services/PaymentService";
import { Format } from "@/data/util/Format";
import { Misc } from "@/data/util/Misc";



import { translations } from "@/shared/localization/translations";
import { logError } from "@/shared/logging/errors";
import { Ripple } from "@/shared/ui/Ripple";
import { toMoneyWithSign } from "@/shared/utils/format";
import { type VueComponentInstance, createVueComponent } from "@/shared/utils/vue";



import ContactDataSection from "@/components/pay/ContactDataSection.vue";
import DeliveryAddressSection from "@/components/pay/DeliveryAddress/DeliveryAddressSection.vue";
import ApplyDiscountSection from "@/components/vouchers/ApplyDiscountSection.vue";



import { config } from "@/app/App";
import { TastierClient } from "@/app/TastierClient";
import AppTipSummaryTable from "@/app/components/payment/AppTipSummaryTable.vue";
import KioskQrCodeOverlay from "@/app/components/kiosk/KioskQrCodeOverlay.vue";
import KioskPaymentMethodSelector from "@/app/components/payment/KioskPaymentMethodSelector.vue";
import { BaseController } from "@/app/controller/BaseController";
import { PayButton } from "@/app/elements/PayButton";
import { Slider } from "@/app/elements/Slider";
import { appEvents } from "@/app/events/appEvents";
import { globalStores } from "@/app/globalStores";
import { trans } from "@/app/localization/Trans";
import { UserCreditCardsService } from "@/app/services/userCreditCardsService";
import { Helper } from "@/app/ui/Helper";
import { Popup } from "@/app/ui/Popup";
import { UserMenu } from "@/app/ui/UserMenu";


type State = {
    consumption: Ref<number>;
    discount: Ref<number>;
    tip: Ref<number>;
    fees: Ref<number>;
    totalAfterDiscount: Ref<number>;
    total: Ref<number>;
    isPayService: Ref<boolean>;
    showTip: Ref<boolean>;
    hasDiscounts: ComputedRef<boolean>;
};

// pay view used only in kiosk mode: all available methods are shown by the kiosk selector,
// there is no quick selector, no method selector popover and no sign up before pay
export class KioskPayController extends BaseController {
    public onGoBack?: { () };

    private readonly customerDao: CustomerDao;
    private readonly $tip: JQuery;
    private readonly $tipLabel: JQuery;
    private readonly $title: JQuery;

    private tipSlider: Slider;
    private useTip: boolean = true;

    private kioskPaymentMethodSelector: VueComponentInstance<typeof KioskPaymentMethodSelector>;

    private $applyDiscountSectionContainer!: JQuery;
    private applyDiscountSectionComponent?: VueComponentInstance<typeof ApplyDiscountSection>;
    private contactDataSection: VueComponentInstance<typeof ContactDataSection>;

    private $contactDataSection: JQuery;

    private $deliveryAddressSection: JQuery;

    private readonly $bottomContainer: JQuery;
    private readonly $disclaimer: JQuery;

    private payBtn: PayButton;
    public userMenu: UserMenu;
    public shouldShowSelectionsChangedPopupOnLeave: boolean = false;

    public $inputContainer: JQuery;
    private $newTotal: JQuery;

    private currentState: State = {
        consumption: ref(0),
        discount: ref(0),
        tip: ref(0),
        fees: ref(0),
        totalAfterDiscount: ref(0),
        total: ref(0),
        isPayService: ref(false),
        showTip: ref(true),
        hasDiscounts: computed(() =>
            this.currentState.isPayService.value
                ? globalStores.billStore!.hasDiscounts
                : globalStores.cartStore!.hasDiscounts
        ),
    };

    constructor() {
        super("pay");
        const hdr = $('<div class="head"></div>').appendTo(this.$view);
        this.$title = $('<div class="title"></div>').appendTo(hdr);

        this.customerDao = Factory.getInstance<CustomerDao>(CustomerDao);

        this.updateTitle(TastierClient.payContext.isPayLater());

        Ripple.bind($('<a class="action left" href="#"><i class="fal fa-arrow-left"></i></a>'))
            .prependTo(hdr)
            .click((e) => {
                e.preventDefault();

                const statesToSkip = [UiPaymentState.Paying, UiPaymentState.Confirmed, UiPaymentState.Failed];
                if (statesToSkip.includes(TastierClient.payContext.state)) {
                    return;
                }

                if (this.onGoBack) {
                    this.onGoBack();
                }
            });

        this.userMenu = new UserMenu().appendTo(hdr);
        const $scrollable = $('<div class="scrollable" />').appendTo(this.$view);
        this.$tip = $('<div class="tip" />').appendTo($scrollable);

        const tipRow = $('<div class="row" />').appendTo(this.$tip);
        this.$tipLabel = $('<div class="label" />').appendTo(tipRow);

        this.tipSlider = new Slider(config.maxTip).setValue(TastierClient.payContext.tipPercent!).appendTo(tipRow);
        this.tipSlider.onChange = (v) => {
            TastierClient.payContext.setTipInPercent(v);
        };

        this.$inputContainer = $('<div class="inputs-container"></div>').appendTo(this.$view);



        this.initializeApplyDiscountSection();

        this.$contactDataSection = $('<div style="margin-bottom: 6px;" class="contact-data form"></div>').appendTo(
            this.$inputContainer
        );

        this.contactDataSection = createVueComponent(
            ContactDataSection,
            {
                isInOperatorApp: false,
                onClickOpenProfileView: () => {
                    const ordererRequirements = Helper.getCurrentOrderServiceOrderApplication()?.ordererRequirements;
                    this.userMenu.showProfile(ordererRequirements?.isPhoneNumberRequired);
                },
                onStartEditing: () => {
                    this.setApplyDiscountSectionVisibility(true);

                    this.showHideTip(true);
                },
                onEndEditing: () => {
                    this.setApplyDiscountSectionVisibility(false);
                    this.showHideTip(false);
                },
            },
            this.$contactDataSection[0]
        ).mountedComponent;

        this.$deliveryAddressSection = $(
            '<div style="margin-bottom: 6px;" class="delivery-address form"></div>'
        ).appendTo(this.$inputContainer);

        createVueComponent(DeliveryAddressSection, {}, this.$deliveryAddressSection[0]).mountedComponent;

        this.$newTotal = $("<div></div>").appendTo(this.$inputContainer);
        const props = {
            totalConsumption: this.currentState.consumption,
            discountAmount: this.currentState.discount,
            tip: this.currentState.tip,
            fees: this.currentState.fees,
            showTip: this.currentState.showTip,
            isDiscountClickable: ref(true),
            hasDiscounts: this.currentState.hasDiscounts,
            isPayService: this.currentState.isPayService,
            total: this.currentState.total,
            onDiscountRemoved: () => {
                this.updateCurrentState();
            },
        };

        // Tip Summary Table Component
        createVueComponent(AppTipSummaryTable, props, this.$newTotal[0]).mountedComponent;

        this.$bottomContainer = $('<div class="bottom" />').appendTo(this.$view);
        this.$disclaimer = $('<div class="disclaimer" />').appendTo(this.$bottomContainer);

        // the kiosk may not leave the app for a browser, so a link of the disclaimer is shown as a
        // QR code to read on the guest's own phone. delegated, because updateState replaces the
        // markup with every language change
        this.$disclaimer.on("click", "a", (e) => {
            e.preventDefault();
            this.showQrCode((e.currentTarget as HTMLAnchorElement).href);
        });

        // there is no method selector popover that could take the space next to the pay button
        this.$bottomContainer.addClass("moveaside");

        // hidden: picking a payment method pays straight away, so there is nothing left for
        // the pay button to confirm. it is still built, because the pay flow drives its
        // state (paying / successful / failed) further down
        const btnContainer = $('<div class="btncontainer" />').appendTo(this.$bottomContainer).hide();

        this.payBtn = new PayButton(translations.pay.payNow).appendTo(btnContainer);
        this.payBtn.onClick = async () => {
            await this.validateAndPay();
        };

        // the methods are the main action of this view, so they come first - above the discount,
        // contact data and delivery address sections
        const $kioskPaymentMethodSelector = $("<div class='kiosk-payment-selector'></div>").appendTo(this.$view);
        this.kioskPaymentMethodSelector = createVueComponent(
            KioskPaymentMethodSelector,
            {
                onSelect: async (selectedMethod: IPaymentMethod) => {
                    await this.onKioskPaymentMethodSelected(selectedMethod);
                },
            },
            $kioskPaymentMethodSelector[0]
        ).mountedComponent;
    }

    private initializeApplyDiscountSection() {
        if (TastierClient.context.currentAllocation == null) throw new Error("User is not allocated");

        const props = {
            enableQrScanner: true,
            inOperatorApp: false,
            locationId: TastierClient.context.currentAllocation!.locationId,
            orderServiceId: TastierClient.context.selectedServiceId,
            onApplyDiscountErrorOccurred: (error: DaoError) => onApplyDiscountErrorOccurred(error),
        };

        this.$applyDiscountSectionContainer = $("<div></div>").appendTo(this.$inputContainer);
        this.applyDiscountSectionComponent = createVueComponent(
            ApplyDiscountSection,
            props,
            this.$applyDiscountSectionContainer[0]
        ).mountedComponent;
    }

    protected async onBecomeVisible(data?: { isMainNavigation: boolean; isForPayService: boolean }) {
        const isPayService = Helper.isPayService();
        const isInit = data?.isMainNavigation == true;

        // register event handlers
        TastierClient.payContext.onAmountChanged.add(this.updateAmount, this);
        TastierClient.payContext.onStateChanged.add(this.updateState, this);
        TastierClient.payContext.onIntentChanged.add(this.intentUpdated, this);
        TastierClient.identityContext.onIdentityChange.add(this.onAuthenticationChanged, this);
        UserCreditCardsService.onCreditCardsChanged.add(this.onCreditCardsChanged, this);

        // notify about a cart when user uses order service
        // notify about item selections changed when user uses pay service
        if (isPayService) {
            dataEvents.appSocket.ItemSelectionChanged.add(this.onItemSelectionChanged, this);
            TastierClient.payContext.onBillDiscountChanged.add(this.updateCurrentState, this);
        } else {
            TastierClient.context.onCartChanged.add(this.onCartUpdated, this);
            TastierClient.context.onCartDiscountChanged.add(this.updateCurrentState, this);
        }

        // Set up the component: Apply discount
        this.applyDiscountSectionComponent?.reset();

        this.contactDataSection?.reset();

        if (isPayService) {
            this.applyDiscountSectionComponent?.setBillId(TastierClient.context.currentBill!.id);
        } else {
            this.applyDiscountSectionComponent?.setCartId(TastierClient.context.getCurrentCartId());
        }

        // if user didn't come from main view - no need to init state, only recover it
        if (isInit) {
            // set initial state - the state that user sees and amounts that user agreed to pay
            // this shouldn't change after user enters this page
            TastierClient.payContext.renewConsumption();
            this.currentState.consumption.value = TastierClient.payContext.consumption;
            this.currentState.discount.value = TastierClient.payContext.discountAmount;
            this.currentState.fees.value = TastierClient.payContext.fees;
            this.currentState.totalAfterDiscount.value = TastierClient.payContext.totalExclTipAfterDiscounts;
            this.currentState.isPayService.value = Helper.isPayService();

            this.shouldShowSelectionsChangedPopupOnLeave = false;
        }

        // todo: do not remove! without it the consumption is not being updated
        this.enableDisableTip();

        await this.prepareForCheckout();

        this.updateAmount();
        this.updateState();

        this.setApplyDiscountSectionVisibility();

        appEvents.payContext.totalDiscountMismatchPopupViewed.add(this.onTotalDiscountMismatchPopupViewed, this);
    }

    protected onBecomeInvisible() {
        TastierClient.payContext.onAmountChanged.remove(this.updateAmount, this);
        TastierClient.payContext.onStateChanged.remove(this.updateState, this);
        TastierClient.payContext.onIntentChanged.remove(this.intentUpdated, this);
        TastierClient.identityContext.onIdentityChange.remove(this.onAuthenticationChanged, this);
        UserCreditCardsService.onCreditCardsChanged.remove(this.onCreditCardsChanged, this);
        TastierClient.context.onCartChanged.remove(this.onCartUpdated, this);
        dataEvents.appSocket.ItemSelectionChanged.remove(this.onItemSelectionChanged, this);

        TastierClient.context.onCartDiscountChanged.remove(this.updateCurrentState, this);
        TastierClient.payContext.onBillDiscountChanged.remove(this.updateCurrentState, this);

        //Reset pay button
        this.payBtn.setAmount(toMoneyWithSign(0));

        appEvents.payContext.totalDiscountMismatchPopupViewed.remove(this.onTotalDiscountMismatchPopupViewed);
    }

    private async validateAndPay() {
        const contactDataValidationResult = await this.contactDataSection?.validateAndCreateOrUpdateContactData(true);
        const ordererRequirements = Helper.getCurrentOrderServiceOrderApplication()?.ordererRequirements;

        if (
            (ordererRequirements?.enabledFieldRequirementsCount ?? 0) > 0 &&
            contactDataValidationResult.isValid == false
        ) {
            return;
        }

        // Confirm email if required / optional and provided
        if (ordererRequirements?.isEmailRequirementEnabled === true) {
            const contactData = contactDataValidationResult.contactData;
            if (contactData!.email != null && contactData!.email.length > 0) {
                this.showIsEmailCorrectPopup(contactData!.email, async () => {
                    await this.payForSelectedItems();
                });
                return;
            }
        }

        await this.payForSelectedItems();
    }

    private setApplyDiscountSectionVisibility(forceHide: boolean = false) {
        const method = TastierClient.payContext.selectedPaymentMethod;
        const isPayService = Helper.isPayService();
        const showApplyDiscountSection = isPayService
            ? getIsApplyVouchersAllowedForPayUseCase(method, Helper.getPayUseCase())
            : getIsApplyVouchersAllowedForOrderUseCase(method, Helper.getCurrentOrderServiceOrderApplication());
        if (!forceHide && showApplyDiscountSection) {
            this.$applyDiscountSectionContainer.show();
        } else {
            this.$applyDiscountSectionContainer.hide();
        }
    }

    private async payForSelectedItems() {
        const selectedItems = TastierClient.context.getSelectedItems();

        await TastierClient.payContext.pay({
            selectedItems,
            consumption: this.getConsumption(),
            totalDiscount: this.currentState.discount.value,
            force: false,
            onClickBackBtn: () => {
                if (this.onGoBack) {
                    this.onGoBack();
                }
            },
        });
    }

    private showIsEmailCorrectPopup(email: string, onConfirm: () => void) {
        Popup.show(
            translations.pay.isEmailCorrectQuestion.replace("{0}", email),
            onConfirm,
            translations.general.yes,
            translations.general.cancel,
            "email-confirmation-popup",
            false,
            true,
            true
        );
    }

    private updateAmount() {
        // we calculate total here, because consumption should not change after entering this page!
        const tip = TastierClient.payContext.tipAmount ?? 0;
        const fees = TastierClient.payContext.fees ?? 0;
        const total = Misc.roundCents(this.getTotalAfterDiscount() + tip + fees);
        // the pay button is hidden, the tip summary table shows this figure instead
        this.currentState.total.value = total;
        this.payBtn.setAmount(toMoneyWithSign(total, true));

        this.currentState.tip.value = tip;
        this.currentState.fees.value = fees;
    }

    private getConsumption() {
        return this.currentState.consumption.value;
    }

    private getTotalAfterDiscount() {
        return this.currentState.totalAfterDiscount.value;
    }

    private updateState() {
        const isPayLater = TastierClient.payContext.isPayLater();

        this.updateTitle(isPayLater);

        // the controller is built once and lives as long as the page, so every text that is not
        // rebuilt with its markup has to be re-applied here - otherwise it keeps the language of
        // the guest that first reached this view. the disclaimer carries links, so it is set as
        // html and not as text
        this.$disclaimer.html(trans.pay.disclaimer);
        this.$tipLabel.text(translations.pay.tip);

        if (!this.useTip || !TastierClient.payContext.isTipAllowedForSelectedPaymentMethod()) {
            this.enableDisableTip();
        }

        this.updatePayButtonState(isPayLater);
        this.kioskPaymentMethodSelector.setSelectedMethod(TastierClient.payContext.selectedPaymentMethod?.id);
        this.currentState.isPayService.value = Helper.isPayService();
    }

    private showQrCode(url: string) {
        const $host = $('<div class="kiosk-qr-overlay-host" />').appendTo(".app");
        const qrCodeOverlay = createVueComponent(
            KioskQrCodeOverlay,
            {
                url,
                onClose: () => {
                    qrCodeOverlay.componentApp.unmount();
                    $host.remove();
                },
            },
            $host[0]
        );
    }

    private updateTitle(isPayLater: boolean) {
        if (isPayLater) {
            this.$title.text(trans.pay.order);
        } else {
            this.$title.text(trans.pay.title);
        }
    }

    private updatePayButtonState(isPayLater: boolean) {
        switch (TastierClient.payContext.state) {
            case UiPaymentState.Preparing:
                this.payBtn.setDisabled();
                this.payBtn.setAction(translations.pay.payNow);
                break;
            case UiPaymentState.MethodSelection:
                this.kioskPaymentMethodSelector.setEnabled();
                this.payBtn.setDisabled(undefined, true);
                this.payBtn.setAction(translations.pay.payNow);
                break;
            case UiPaymentState.ReadyToPay:
                this.payBtn.setEnabled();
                this.kioskPaymentMethodSelector.setEnabled();
                if (isPayLater) {
                    this.payBtn.setAction(trans.pay.order);
                } else {
                    this.payBtn.setAction(translations.pay.payNow);
                }
                break;
            case UiPaymentState.Paying:
                this.payBtn.setDisabled(trans.pay.paying);
                this.kioskPaymentMethodSelector.setDisabled();
                break;
            case UiPaymentState.Confirmed:
                this.payBtn.setDisabled(trans.pay.btnSuccessful);
                this.kioskPaymentMethodSelector.setDisabled();
                break;
            case UiPaymentState.Failed:
                this.payBtn.setDisabled(trans.pay.btnFailed);
                this.kioskPaymentMethodSelector.setDisabled();
                break;
            case UiPaymentState.Ordering:
                this.payBtn.setDisabled(trans.pay.ordering);
                this.kioskPaymentMethodSelector.setDisabled();
                break;
        }
    }

    private onItemSelectionChanged(eventData: BillItemSelectionChangedEvent) {
        if (eventData.identityId !== TastierClient.identityContext.getIdentityId()) {
            return;
        }

        if (eventData.reason == UserItemSelectionChangeReason.SelectionsReset) {
            this.shouldShowSelectionsChangedPopupOnLeave = true;
        }
    }

    // in kiosk mode selecting a method only marks it, the payment is started with the pay button
    // the method buttons are the action here, like the waiter's quick payment buttons:
    // picking one pays with it straight away instead of arming a separate pay button
    private async onKioskPaymentMethodSelected(selectedMethod: IPaymentMethod) {
        if (selectedMethod.type === "OpenCashDrawer") return;

        TastierClient.payContext.setPaymentMethod(selectedMethod, undefined);
        this.setApplyDiscountSectionVisibility();
        await this.validateAndPay();
    }

    private intentUpdated(intent: IPaymentIntent) {
        this.kioskPaymentMethodSelector.setPaymentMethods(intent.paymentMethods);
        this.kioskPaymentMethodSelector.setPaymentPrompt(intent.kioskPaymentPrompt);
    }

    private async onAuthenticationChanged() {
        if (this.isPayingState()) {
            return;
        }

        await this.prepareForCheckout();
    }

    private async onCreditCardsChanged() {
        if (this.isPayingState()) {
            return;
        }

        await this.prepareForCheckout();
    }

    private async prepareForCheckout() {
        try {
            await TastierClient.payContext.prepareForCheckout();
        } catch (ex) {
            logError(ex);
        }
    }

    private isPayingState() {
        return TastierClient.payContext.state === UiPaymentState.Paying;
    }

    // the kiosk starts the payment from the method buttons, so this view is up both before and
    // during it. once it is under way the session must not be reset underneath it, the same way
    // the back button in the constructor stops the guest from leaving
    public isPaymentUnderWay() {
        const statesUnderWay = [
            UiPaymentState.Paying,
            UiPaymentState.Ordering,
            UiPaymentState.Confirmed,
            UiPaymentState.Failed,
        ];

        return statesUnderWay.includes(TastierClient.payContext.state);
    }

    private updateCurrentState() {
        TastierClient.payContext.renewConsumption();
        this.currentState.totalAfterDiscount.value = TastierClient.payContext.totalExclTipAfterDiscounts;
        this.currentState.consumption.value = TastierClient.payContext.consumption;
        this.currentState.discount.value = TastierClient.payContext.discountAmount;
        this.currentState.fees.value = TastierClient.payContext.fees;
        this.currentState.isPayService.value = Helper.isPayService();

        this.updateAmount();
    }

    private onCartUpdated(isUserAction: boolean) {
        // don't notify if cart is already checked out / payment going on
        const statesToSkip = [UiPaymentState.Paying, UiPaymentState.Confirmed, UiPaymentState.Failed];

        if (statesToSkip.includes(TastierClient.payContext.state)) return;

        if (isUserAction)
            return;

        Popup.show(
            "<b>" +
                Format.htmlEscape(translations.pay.cartChangedTitle) +
                "</b><br /><br />" +
                Format.htmlEscape(translations.pay.cartChangedMessage),
            () => {
                if (this.onGoBack !== undefined) this.onGoBack();
            },
            undefined,
            undefined,
            "totalChanged"
        );
    }

    private showHideTip(forceHide: boolean = false) {
        if (!forceHide && this.useTip) {
            this.$tip.show();
        } else {
            this.$tip.hide();
        }
    }

    private enableDisableTip(forceHide: boolean = false) {
        if (forceHide) {
            this.$tip.hide();
            return;
        }

        const useCase = Helper.getCurrentUseCase();
        this.useTip =
            TastierClient.payContext.consumption >= 0 &&
            useCase?.useTip == true &&
            TastierClient.payContext.isTipAllowedForSelectedPaymentMethod();

        if (this.useTip) {
            const newTipPercent = this.$tip.is(":visible")
                ? TastierClient.payContext.tipPercent
                : TastierClient.payContext.defaultTipPercent;

            this.$tip.show();
            this.currentState.showTip.value = true;
            this.tipSlider.setValue(newTipPercent!).resize();
            TastierClient.payContext.setTipInPercent(newTipPercent!);
        } else {
            this.currentState.showTip.value = false;
            TastierClient.payContext.setTipInPercent(0);
            this.$tip.hide();
            this.tipSlider.setValue(0);
        }
    }

    private async onTotalDiscountMismatchPopupViewed() {
        if (this.currentState.isPayService.value) {
            if (TastierClient.context.currentBill?.id) {
                const currentBill = await this.customerDao.getCurrentBill({
                    billId: TastierClient.context.currentBill?.id,
                });
                this.currentState.discount.value = currentBill?.selectionTotalDiscountsAndVouchersAmount ?? 0;
                this.currentState.totalAfterDiscount.value =
                    this.currentState.consumption.value - this.currentState.discount.value;
            }
        } else {
            try {
                const currentCart = await this.customerDao.getCart();
                this.currentState.discount.value = currentCart?.totalDiscountedAmount ?? 0;
                this.currentState.totalAfterDiscount.value =
                    this.currentState.consumption.value - this.currentState.discount.value;
            } catch (ex) {
                logError(ex);
            }
        }
        this.updateAmount();
    }
}
